import React, { useEffect, useState } from 'react';
import { Table, Button, Modal, Form, Input, message, Popconfirm, Select, Upload, Tooltip, Avatar, Row, Col, notification, Divider, Space, Tag, Card, List, Typography } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, ApartmentOutlined, FileOutlined, UploadOutlined, MinusCircleOutlined } from '@ant-design/icons';
import {
  getClients,
  getClient,
  createClient,
  updateClient,
  deleteClient,
  bulkUploadClientDocuments,
  deleteClientDocument,
  imagepath
} from '../api';
import ClientDepartments from './ClientDepartments';

const { Option } = Select;
const { Text } = Typography;

const clientDocTypes = [
  { value: 'license', label: 'License' },
  { value: 'work_contract', label: 'Work Contract' },
  { value: 'other', label: 'Other' },
];

const isImage = (fileName) => /\.(jpg|jpeg|png|gif)$/i.test(fileName);
const isPdf = (fileName) => /\.(pdf)$/i.test(fileName);

// Helper to get initials from name
function getInitials(name) {
  if (!name) return '';
  const parts = name.trim().split(' ');
  if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

// Helper to get a color from a string
function getColor(str) {
  const colors = ['#1890ff', '#f5222d', '#faad14', '#52c41a', '#722ed1', '#13c2c2', '#eb2f96', '#b37feb'];
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
}

const Clients = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const [modalVisible, setModalVisible] = useState(false);
  const [docViewModalVisible, setDocViewModalVisible] = useState(false);
  const [viewingClient, setViewingClient] = useState(null);
  const [initialValues, setInitialValues] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form] = Form.useForm();
  const [selectedClientId, setSelectedClientId] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [saving, setSaving] = useState(false);

  const fetchClients = async () => {
    setLoading(true);
    const res = await getClients();
    setLoading(false);
    if (res.success) {
      setClients(res.data);
    } else {
      message.error(res.message || 'Failed to fetch clients');
    }
  };

  useEffect(() => {
    fetchClients();
  }, []);

  const openModal = async (record = null) => {
    setEditing(record);
    if (record) {
      setLoading(true);
      const res = await getClient(record.id);
      setLoading(false);
      if (res.success) {
        const clientData = { ...res.data };
        if (clientData.documents && clientData.documents.length > 0) {
          clientData.documents = clientData.documents.map(doc => ({
            ...doc,
            document_file: doc.document_file ? [{
              uid: doc.id,
              name: doc.original_filename || doc.document_file.split('/').pop(),
              status: 'done',
              url: `${imagepath}${doc.document_file}`,
            }] : []
          }));
        }
        setInitialValues(clientData);
        form.setFieldsValue(clientData);
      }
    } else {
      setInitialValues(null);
      form.resetFields();
    }
    setModalVisible(true);
    setLogoFile(null);
  };

  const handleOk = async () => {
    try {
      setSaving(true);
      const values = await form.validateFields();

      let formData = new FormData();
      Object.entries(values).forEach(([key, value]) => {
        if (key !== 'documents' && key !== 'logo') {
          formData.append(key, value || '');
        }
      });

      if (logoFile) {
        formData.append('logo', logoFile);
      }

      let res;
      let clientId;
      if (editing) {
        // 1. Delete removed documents
        const removedDocIds = (initialValues.documents || []).filter(initDoc => {
          const currentDoc = values.documents?.find(d => d.id === initDoc.id);
          if (!currentDoc) return true;
          const hasNewFile = currentDoc.document_file?.[0]?.originFileObj;
          const hasNoFile = !currentDoc.document_file || currentDoc.document_file.length === 0;
          return !!(hasNewFile || hasNoFile);
        }).map(doc => doc.id);

        if (removedDocIds.length > 0) {
          await Promise.all(removedDocIds.map(docId => deleteClientDocument(editing.id, docId)));
        }

        res = await updateClient(editing.id, formData);
        clientId = editing.id;
      } else {
        res = await createClient(formData);
        clientId = res.data?.id;
      }

      if (res.success) {
        // 2. Upload new documents
        if (values.documents && values.documents.length > 0) {
          const docsToUpload = values.documents
            .filter(doc => doc.document_file?.[0]?.originFileObj)
            .map(doc => ({
              document_type: doc.document_type,
              issue_date: doc.issue_date,
              expiry_date: doc.expiry_date,
              remarks: doc.remarks,
              document_file: doc.document_file[0].originFileObj
            }));

          if (docsToUpload.length > 0) {
            await bulkUploadClientDocuments(clientId, docsToUpload);
          }
        }

        notification.success({
          message: editing ? 'Client updated' : 'Client created',
          placement: 'bottomRight',
          duration: 2
        });
        setModalVisible(false);
        fetchClients();
      } else {
        throw new Error(res.message);
      }
    } catch (err) {
      console.error(err);
      notification.error({
        message: err.message || 'Operation failed',
        placement: 'bottomRight',
        duration: 2
      });
    } finally {
      setSaving(false);
    }
  };

  const openDocViewModal = async (record) => {
    setLoading(true);
    const res = await getClient(record.id);
    setLoading(false);
    if (res.success) {
      setViewingClient(res.data);
      setDocViewModalVisible(true);
    }
  };

  const handleDelete = async (id) => {
    const res = await deleteClient(id);
    if (res.success) {
      message.success('Client deleted');
      fetchClients();
    } else {
      message.error(res.message || 'Delete failed');
    }
  };

  const columns = [
    {
      title: 'S.No',
      key: 'sno',
      width: 60,
      render: (_, __, index) => (pagination.current - 1) * pagination.pageSize + index + 1
    },

    {
      title: 'Logo',
      dataIndex: 'logo',
      key: 'logo',
      width: 80,
      render: (logo, record) => {
        if (logo) {
          return <img src={imagepath + logo} alt="logo" style={{ width: 64, height: 64, objectFit: 'contain', borderRadius: '50%' }} />;
        } else {
          return (
            <Avatar style={{ backgroundColor: getColor(record.name), verticalAlign: 'middle', fontWeight: 600 }} size={64}>
              {getInitials(record.name)}
            </Avatar>
          );
        }
      },
    },
    { title: 'Name', dataIndex: 'name', key: 'name' },
    { title: 'Email', dataIndex: 'email', key: 'email' },
    { title: 'Phone', dataIndex: 'phone', key: 'phone' },
    { title: 'Contact Person', dataIndex: 'contact_person', key: 'contact_person' },
    { title: 'Status', dataIndex: 'status', key: 'status', width: 100 },
    {
      title: 'Actions',
      key: 'actions',
      width: 250,
      render: (_, record) => (
        <>
          <Tooltip title="Edit Client">
            <Button icon={<EditOutlined />} size="small" style={{ marginRight: 8 }} onClick={() => openModal(record)} />
          </Tooltip>
          <Tooltip title="Documents">
            <Button icon={<FileOutlined />} size="small" style={{ marginRight: 8, color: '#1890ff' }} onClick={() => openDocViewModal(record)} />
          </Tooltip>
          <Tooltip title="Delete Client">
            <Popconfirm title="Are you sure to delete this client?" onConfirm={() => handleDelete(record.id)} okText="Yes" cancelText="No">
              <Button icon={<DeleteOutlined />} size="small" danger style={{ marginRight: 8 }} />
            </Popconfirm>
          </Tooltip>
          <Tooltip title="View Departments">
            <Button icon={<ApartmentOutlined />} size="small" onClick={() => setSelectedClientId(record.id)} />
          </Tooltip>
        </>
      ),
    },
  ];

  if (selectedClientId) {
    return <ClientDepartments clientId={selectedClientId} onBack={() => setSelectedClientId(null)} />;
  }

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
        <h2>Clients</h2>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => openModal()}>Add Client</Button>
      </div>
      <Table
        rowKey="id"
        columns={columns}
        dataSource={clients}
        loading={loading}
        bordered
        pagination={pagination}
        onChange={(p) => setPagination(p)}
      />
      <Modal
        title={editing ? 'Edit Client' : 'Add Client'}
        open={modalVisible}
        onOk={handleOk}
        onCancel={() => setModalVisible(false)}
        okText={editing ? 'Update' : 'Create'}
        confirmLoading={saving}
        width="80vw"
        style={{ height: '70vh', maxWidth: '100vw', padding: 0 }}
        bodyStyle={{ height: 'calc(70vh - 55px)', overflow: 'auto', margin: 0, padding: 24 }}
      >
        <Form form={form} layout="vertical">
          <Row gutter={24}>
            <Col span={12}>
              <Form.Item name="name" label="Name" rules={[{ required: true, message: 'Please enter name' }]}>
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="email" label="Email" rules={[
                { required: true, message: 'Please enter email' },
                { type: 'email', message: 'Invalid email' }
              ]}>
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="phone"
                label="Contact Number"
                getValueFromEvent={(e) => e.target.value.replace(/\D/g, '')}
                rules={[
                  { required: true, message: 'Please enter contact number' },
                  { pattern: /^[0-9]+$/, message: 'Contact number must contain numbers only' }
                ]}
              >
                <Input inputMode="numeric" placeholder="e.g. 0501234567" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="address" label="Address">
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="contact_person" label="Contact Name" rules={[{ required: true, message: 'Please enter contact name' }]}>
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="status" label="Status" initialValue="active">
                <Select>
                  <Option value="active">Active</Option>
                  <Option value="inactive">Inactive</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item label="Logo">
                <Upload
                  beforeUpload={file => {
                    const isLt5M = file.size / 1024 / 1024 <= 5;
                    if (!isLt5M) {
                      message.error(`"${file.name}" exceeds the 5MB limit (${(file.size / 1024 / 1024).toFixed(2)}MB). Please select a file under 5MB.`);
                      return Upload.LIST_IGNORE;
                    }
                    setLogoFile(file);
                    return false;
                  }}
                  showUploadList={logoFile ? [{ name: logoFile.name }] : false}
                  maxCount={1}
                  accept=".jpg,.jpeg,.png,.gif"
                >
                  <Button>Choose Logo</Button>
                </Upload>
                {editing && editing.logo && !logoFile && (
                  <img src={imagepath + 'storage/' + editing.logo} alt="logo" style={{ width: 60, marginTop: 8 }} />
                )}
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left" style={{ borderColor: '#1890ff', marginTop: 32 }}>
            <Space><FileOutlined /> Client Documents</Space>
          </Divider>

          <div style={{ background: '#f9f9f9', padding: 20, borderRadius: 12, border: '1px solid #f0f0f0' }}>
            <Form.List name="documents">
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name, ...restField }) => (
                    <Card
                      key={key}
                      size="small"
                      style={{ marginBottom: 16, border: '1px solid #e8e8e8', borderRadius: 10, boxShadow: '0 2px 4px rgba(0,0,0,0.02)' }}
                      title={<Tag color="blue">{form.getFieldValue(['documents', name, 'document_type'])?.replace(/_/g, ' ').toUpperCase() || 'NEW DOCUMENT'}</Tag>}
                      extra={<MinusCircleOutlined onClick={() => remove(name)} style={{ color: '#ff4d4f', fontSize: 18 }} />}
                    >
                      <Row gutter={16}>
                        <Col span={5}>
                          <Form.Item
                            {...restField}
                            name={[name, 'document_type']}
                            label="Document Type"
                            rules={[{ required: true, message: 'Type required' }]}
                          >
                            <Select placeholder="Select type" onChange={() => form.setFieldsValue({ documents: form.getFieldValue('documents') })}>
                              {clientDocTypes.map(dt => <Option key={dt.value} value={dt.value}>{dt.label}</Option>)}
                            </Select>
                          </Form.Item>
                        </Col>
                        <Col span={5}>
                          <Form.Item
                            {...restField}
                            name={[name, 'issue_date']}
                            label="Issue Date"
                            rules={[
                              ({ getFieldValue }) => ({
                                validator(_, value) {
                                  const docType = getFieldValue(['documents', name, 'document_type']);
                                  if (docType === 'work_contract' && !value) {
                                    return Promise.reject(new Error('Issue date is required for Work Contract'));
                                  }
                                  return Promise.resolve();
                                }
                              })
                            ]}
                            getValueProps={(value) => ({
                              value: value ? new Date(value).toISOString().split('T')[0] : ''
                            })}
                          >
                            <Input type="date" />
                          </Form.Item>
                        </Col>
                        <Col span={5}>
                          <Form.Item
                            {...restField}
                            name={[name, 'expiry_date']}
                            label="Expiry Date"
                            rules={[
                              { required: true, message: 'Expiry date is required' },
                              () => ({
                                validator(_, value) {
                                  if (value && new Date(value) < new Date(new Date().setHours(0, 0, 0, 0))) {
                                    return Promise.reject(new Error('Expiry date cannot be in the past'));
                                  }
                                  return Promise.resolve();
                                }
                              })
                            ]}
                            getValueProps={(value) => ({
                              value: value ? new Date(value).toISOString().split('T')[0] : ''
                            })}
                          >
                            <Input type="date" min={new Date().toISOString().split('T')[0]} />
                          </Form.Item>
                        </Col>
                        <Col span={5}>
                          <Form.Item
                            {...restField}
                            name={[name, 'document_file']}
                            label="File Upload"
                            valuePropName="fileList"
                            getValueFromEvent={e => (Array.isArray(e) ? e.slice(-1) : e && e.fileList ? e.fileList.slice(-1) : [])}
                            rules={[{ required: true, message: 'File required' }]}
                          >
                            <Upload
                              beforeUpload={(file) => {
                                const isLt5M = file.size / 1024 / 1024 <= 5;
                                if (!isLt5M) {
                                  message.error(`"${file.name}" exceeds the 5MB limit (${(file.size / 1024 / 1024).toFixed(2)}MB). Please select a file under 5MB.`);
                                  return Upload.LIST_IGNORE;
                                }
                                return false;
                              }}
                              maxCount={1}
                              listType="picture"
                              accept=".jpg,.jpeg,.png,.pdf,.doc,.docx"
                            >
                              <Button icon={<UploadOutlined />} style={{ width: '100%' }}>Choose File</Button>
                            </Upload>
                          </Form.Item>
                        </Col>
                        <Col span={4}>
                          <Form.Item
                            {...restField}
                            name={[name, 'remarks']}
                            label="Remarks"
                          >
                            <Input placeholder="Optional remarks" />
                          </Form.Item>
                        </Col>
                      </Row>
                    </Card>
                  ))}
                  <Form.Item style={{ marginBottom: 0 }}>
                    <Button
                      type="dashed"
                      onClick={() => add()}
                      block
                      icon={<PlusOutlined />}
                      style={{ height: 45, borderRadius: 10, background: '#fff', borderStyle: 'dashed', borderWidth: 2 }}
                    >
                      Add Another Document
                    </Button>
                  </Form.Item>
                </>
              )}
            </Form.List>
          </div>
        </Form>
      </Modal>

      <Modal
        title={`Documents for ${viewingClient?.name}`}
        open={docViewModalVisible}
        onCancel={() => setDocViewModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setDocViewModalVisible(false)}>Close</Button>
        ]}
        width={900}
        bodyStyle={{ maxHeight: '70vh', overflowY: 'auto' }}
      >
        <Row gutter={[16, 16]}>
          {clientDocTypes.map(section => {
            const docs = viewingClient?.documents?.filter(d => d.document_type === section.value) || [];
            return (
              <Col span={12} key={section.value}>
                <Card
                  title={<Space><Tag color="blue">{docs.length}</Tag> {section.label}</Space>}
                  size="small"
                  style={{ height: '100%', borderRadius: 12, border: '1px solid #f0f0f0', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}
                >
                  {docs.length > 0 ? (
                    <List
                      size="small"
                      dataSource={docs}
                      renderItem={doc => (
                        <List.Item
                          extra={
                            <a href={`${imagepath}${doc.document_file}`} target="_blank" rel="noopener noreferrer">
                              <Button type="link" icon={<FileOutlined />}>View</Button>
                            </a>
                          }
                        >
                          <List.Item.Meta
                            title={<Text strong style={{ fontSize: 13 }}>{doc.original_filename}</Text>}
                            description={
                              <Space direction="vertical" size={0}>
                                {doc.issue_date && <Text type="secondary" style={{ fontSize: 12 }}>Issued: {doc.issue_date}</Text>}
                                {doc.expiry_date && <Text type="secondary" style={{ fontSize: 12 }}>Expiry: {doc.expiry_date}</Text>}
                                {doc.remarks && <Text type="secondary" style={{ fontSize: 12 }}>{doc.remarks}</Text>}
                              </Space>
                            }
                          />
                        </List.Item>
                      )}
                    />
                  ) : (
                    <div style={{ textAlign: 'center', color: '#ccc', padding: '20px 0' }}>No {section.label} uploaded</div>
                  )}
                </Card>
              </Col>
            );
          })}
        </Row>
      </Modal>
    </div>
  );
};

export default Clients; 